/**
 * Génération PDF du rapport journalier d'exploitation Royal LT
 * (brief SG-RLT-CDC-MODULES-145 §1.3).
 *
 * Dynamic imports jspdf + jspdf-autotable → chunk séparé Vite.
 */
import type { RapportJournalier } from '@/types';

interface JsPDFInternal {
  internal: { getNumberOfPages: () => number; pageSize: { height: number; width: number } };
  setFontSize: (size: number) => void;
  setFont: (family: string, style?: string) => void;
  setTextColor: (...args: number[]) => void;
  setFillColor: (...args: number[]) => void;
  rect: (x: number, y: number, w: number, h: number, style?: string) => void;
  text: (text: string | string[], x: number, y: number, options?: object) => void;
  splitTextToSize: (text: string, maxWidth: number) => string[];
  addPage: () => void;
  save: (filename: string) => void;
  lastAutoTable?: { finalY: number };
}

const COLOR_PRIMARY: [number, number, number]  = [192, 0, 0];       // Royal LT rouge
const COLOR_HEADER:  [number, number, number]  = [192, 0, 0];
const COLOR_TX_DARK: [number, number, number]  = [40, 40, 40];
const COLOR_TX_GREY: [number, number, number]  = [110, 110, 110];
const COLOR_OK:      [number, number, number]  = [22, 163, 74];
const COLOR_KO:      [number, number, number]  = [220, 38, 38];

const fmtNumber = (n: number, dec = 0) =>
  new Intl.NumberFormat('fr-FR', { maximumFractionDigits: dec, minimumFractionDigits: dec })
    .format(n)
    .replace(/[  ]/g, ' ');
const fmtDateLong = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
const fmtHeure = (iso: string) =>
  new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', hour12: false });

export async function generateRapportJournalierPDF(
  data: RapportJournalier,
  options: { observations?: string; userFullName?: string; userEmail?: string },
): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' }) as unknown as JsPDFInternal;
  const pageWidth = doc.internal.pageSize.width;

  // ── En-tête ──────────────────────────────────────────────────────
  doc.setFillColor(...COLOR_PRIMARY);
  doc.rect(0, 0, pageWidth, 8, 'F');

  doc.setTextColor(...COLOR_TX_DARK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('RAPPORT JOURNALIER D\'EXPLOITATION', 14, 20);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...COLOR_PRIMARY);
  doc.text(data.filiale.fullName || data.filiale.name, 14, 27);

  doc.setFontSize(10);
  doc.setTextColor(...COLOR_TX_GREY);
  doc.text(`Date : ${fmtDateLong(data.date)}`, 14, 34);
  doc.text(`Pays : ${data.filiale.pays} · Devise : ${data.filiale.devise}`, 14, 40);

  doc.setFontSize(8);
  doc.text(`Généré le ${new Date(data.generatedAt).toLocaleString('fr-FR')} · par ${options.userFullName ?? options.userEmail ?? '—'}`, 14, 46);

  // ── Section 1 — Synthèse opérationnelle ──────────────────────────
  autoTable(doc as unknown as object, {
    startY: 52,
    head: [['Indicateur', 'Réalisé', 'Objectif', 'Écart']],
    body: data.syntheseOperationnelle.map((s) => [
      s.indicateur,
      String(s.realise),
      String(s.objectif),
      s.ecart,
    ]),
    margin: { left: 14, right: 14 },
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: COLOR_HEADER, textColor: 255, fontStyle: 'bold', fontSize: 10 },
    alternateRowStyles: { fillColor: [248, 248, 248] },
    columnStyles: {
      0: { fontStyle: 'bold' },
      3: { halign: 'center' },
    },
    didParseCell: (cell: { row: { index: number }; column: { index: number }; cell: { styles: { textColor?: number[] } } }) => {
      if (cell.column.index === 3) {
        const row = data.syntheseOperationnelle[cell.row.index];
        if (row) cell.cell.styles.textColor = row.ok ? COLOR_OK : COLOR_KO;
      }
    },
    didDrawCell: () => {},
  } as Parameters<typeof autoTable>[1]);

  let cursorY = (doc.lastAutoTable?.finalY ?? 100) + 8;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...COLOR_TX_DARK);
  doc.text('1 · Synthèse opérationnelle', 14, 50);

  // ── Section 2 — Détail par mine ──────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('2 · Détail par mine', 14, cursorY);
  cursorY += 2;

  if (data.detailParMine.length > 0) {
    autoTable(doc as unknown as object, {
      startY: cursorY + 2,
      head: [['Mine', 'Nom', 'Voyages', 'Tonnes', 'Camions actifs', 'Incidents']],
      body: data.detailParMine.map((m) => [
        m.code,
        m.nom,
        String(m.voyages),
        fmtNumber(m.tonnes, 1) + ' T',
        String(m.camionsActifs),
        String(m.incidents),
      ]),
      margin: { left: 14, right: 14 },
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: COLOR_HEADER, textColor: 255, fontStyle: 'bold', fontSize: 10 },
      alternateRowStyles: { fillColor: [248, 248, 248] },
      columnStyles: {
        0: { fontStyle: 'bold' },
        2: { halign: 'right' },
        3: { halign: 'right' },
        4: { halign: 'right' },
        5: { halign: 'right' },
      },
    } as Parameters<typeof autoTable>[1]);
    cursorY = (doc.lastAutoTable?.finalY ?? cursorY) + 8;
  } else {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(...COLOR_TX_GREY);
    doc.text('Aucune mine configurée.', 18, cursorY + 8);
    cursorY += 14;
  }

  // ── Section 3 — Revenus estimés ──────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...COLOR_TX_DARK);
  doc.text('3 · Revenus estimés', 14, cursorY);

  autoTable(doc as unknown as object, {
    startY: cursorY + 2,
    body: [
      ['Tonnes transportées',     fmtNumber(data.revenusEstimes.tonnes, 1) + ' T'],
      ['Prix unitaire',           `${data.revenusEstimes.prixUsdParTonne} USD / tonne`],
      ['Revenus estimés (USD)',   '$ ' + fmtNumber(data.revenusEstimes.usd, 0)],
      ['Taux de change',          `1 USD = ${data.revenusEstimes.tauxFcfa} FCFA`],
      ['Revenus estimés (FCFA)',  fmtNumber(data.revenusEstimes.fcfa, 0) + ' FCFA'],
    ],
    margin: { left: 14, right: 14 },
    styles: { fontSize: 9, cellPadding: 3 },
    columnStyles: {
      0: { cellWidth: 70, fontStyle: 'bold', textColor: COLOR_TX_GREY },
      1: { halign: 'right', fontStyle: 'bold' },
    },
    theme: 'plain',
  } as Parameters<typeof autoTable>[1]);
  cursorY = (doc.lastAutoTable?.finalY ?? cursorY) + 8;

  // Nouvelle page si proche du bas
  if (cursorY > 240) { doc.addPage(); cursorY = 20; }

  // ── Section 4 — Incidents & alertes ──────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...COLOR_TX_DARK);
  doc.text('4 · Incidents & alertes du jour', 14, cursorY);

  if (data.incidents.length === 0) {
    autoTable(doc as unknown as object, {
      startY: cursorY + 2,
      body: [['✅ Aucun incident signalé aujourd\'hui.']],
      margin: { left: 14, right: 14 },
      styles: { fontSize: 9, cellPadding: 4, fontStyle: 'italic', textColor: COLOR_OK },
      theme: 'plain',
    } as Parameters<typeof autoTable>[1]);
  } else {
    autoTable(doc as unknown as object, {
      startY: cursorY + 2,
      head: [['Heure', 'N° voyage', 'Camion', 'Mine', 'Chauffeur', 'Type', 'Description']],
      body: data.incidents.map((i) => [
        fmtHeure(i.heure),
        i.numero,
        i.camion,
        i.mine,
        i.chauffeur,
        i.type ?? '—',
        i.description ?? '—',
      ]),
      margin: { left: 14, right: 14 },
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: COLOR_HEADER, textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 248, 248] },
      columnStyles: {
        0: { cellWidth: 16 },
        1: { cellWidth: 26, fontStyle: 'bold' },
        2: { cellWidth: 18 },
        3: { cellWidth: 14 },
        4: { cellWidth: 32 },
        5: { cellWidth: 22 },
      },
    } as Parameters<typeof autoTable>[1]);
  }
  cursorY = (doc.lastAutoTable?.finalY ?? cursorY) + 8;

  // ── Section 5 — Observations DG ──────────────────────────────────
  if (cursorY > 245) { doc.addPage(); cursorY = 20; }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...COLOR_TX_DARK);
  doc.text('5 · Observations Direction Générale', 14, cursorY);
  cursorY += 5;

  if (options.observations && options.observations.trim()) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...COLOR_TX_DARK);
    const lines = doc.splitTextToSize(options.observations.trim(), pageWidth - 28);
    doc.text(lines, 14, cursorY + 3);
  } else {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(...COLOR_TX_GREY);
    doc.text('Aucune observation pour ce rapport.', 14, cursorY + 3);
  }

  // ── Pied de page sur toutes les pages ────────────────────────────
  const pageCount = doc.internal.getNumberOfPages();
  const pageHeight = doc.internal.pageSize.height;
  for (let i = 1; i <= pageCount; i++) {
    (doc as unknown as { setPage: (n: number) => void }).setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(...COLOR_TX_GREY);
    doc.text(
      `${data.filiale.fullName || data.filiale.name} — Document confidentiel`,
      14,
      pageHeight - 8,
    );
    doc.text(`Page ${i} / ${pageCount}`, pageWidth - 14, pageHeight - 8, { align: 'right' });
  }

  // ── Save ─────────────────────────────────────────────────────────
  const filename = `Rapport_Journalier_${data.filiale.name.replace(/\s+/g, '_')}_${data.date}.pdf`;
  doc.save(filename);
}
